import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = { enabled: false, chat_id: "" };

export default function TelegramSettings({ onHealthChange }) {
  const [cfg, setCfg] = useState(EMPTY);
  const [health, setHealth] = useState(null);
  const [token, setToken] = useState("");
  const [tokenTouched, setTokenTouched] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/settings/telegram");
      const { bot_token_set, last_error, last_error_at, last_success_at, failed_notifications, ...rest } = data;
      setCfg(rest);
      setHealth({ bot_token_set, last_error, last_error_at, last_success_at, failed_notifications });
      onHealthChange?.(data);
    } catch (e) {
      toast.error("Nie udało się pobrać ustawień Telegram");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      // bot_token: null = zostaw zapisany token bez zmian
      const payload = { ...cfg, bot_token: tokenTouched ? token : null };
      await api.put("/admin/settings/telegram", payload);
      toast.success("Ustawienia zapisane");
      setToken("");
      setTokenTouched(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    try {
      await api.post("/admin/settings/telegram/test");
      toast.success("Wiadomość testowa wysłana");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      await load();
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <p className="text-[#5C6670] py-6" data-testid="telegram-loading">Ładowanie ustawień…</p>;

  return (
    <div data-testid="telegram-settings" className="max-w-3xl space-y-8 border-t border-[#E5E0D8] pt-8 mt-8">
      {health?.last_error && (
        <div className="border border-[#C88E75] bg-[#FBF3EF] rounded-2xl p-5" data-testid="telegram-error-box">
          <p className="text-overline text-[#C88E75] mb-2">Ostatnia wysyłka nie powiodła się</p>
          <p className="text-sm text-[#2B2B2B] font-mono break-all">{health.last_error}</p>
          {health.last_error_at && (
            <p className="text-xs text-[#5C6670] mt-2">
              {new Date(health.last_error_at).toLocaleString("pl-PL")}
            </p>
          )}
        </div>
      )}

      <div>
        <h2 className="font-display text-2xl text-[#2B2B2B] mb-2">Powiadomienia Telegram</h2>
        <p className="text-sm text-[#5C6670] leading-relaxed">
          Działa niezależnie od powiadomień e-mail — możesz włączyć jedno, drugie albo oba naraz.
        </p>
      </div>

      <TelegramHelp />

      <label className="flex items-center gap-3 cursor-pointer">
        <input
          type="checkbox" checked={cfg.enabled} data-testid="telegram-enabled"
          onChange={(e) => set("enabled", e.target.checked)}
          className="w-4 h-4 accent-[#8A9A86]"
        />
        <span className="text-sm text-[#2B2B2B]">Wysyłaj powiadomienia o nowych zgłoszeniach na Telegram</span>
      </label>

      <div className="grid md:grid-cols-2 gap-5">
        <Field label="Token bota" hint={health?.bot_token_set ? "Token zapisany. Wpisz nowy, żeby go zmienić." : "Od @BotFather, np. 123456789:ABCdefGhIJKlmnOPQRstuVwxYZ"}>
          <input
            type="password" value={token} data-testid="telegram-token"
            placeholder={health?.bot_token_set ? "•••••••••••••••• (zapisany)" : ""}
            onChange={(e) => { setToken(e.target.value); setTokenTouched(true); }}
            className="w-full px-4 py-2.5 rounded-xl border border-[#E5E0D8] bg-white text-sm text-[#2B2B2B] placeholder:text-[#B5AFA5]"
          />
        </Field>
        <Field label="Chat ID" hint="Numeryczny identyfikator rozmowy, do której bot ma pisać">
          <input
            value={cfg.chat_id} data-testid="telegram-chat-id"
            onChange={(e) => set("chat_id", e.target.value)}
            className="w-full px-4 py-2.5 rounded-xl border border-[#E5E0D8] bg-white text-sm text-[#2B2B2B]"
          />
        </Field>
      </div>

      <div className="flex flex-wrap items-center gap-3 pt-2">
        <button
          onClick={save} disabled={saving} data-testid="telegram-save"
          className="px-6 py-2.5 rounded-xl bg-[#2B2B2B] text-white text-sm disabled:opacity-50"
        >
          {saving ? "Zapisywanie…" : "Zapisz ustawienia"}
        </button>
      </div>

      <div className="border-t border-[#E5E0D8] pt-8">
        <h3 className="font-display text-lg text-[#2B2B2B] mb-2">Wyślij wiadomość testową</h3>
        <p className="text-sm text-[#5C6670] mb-4">
          Test używa zapisanych ustawień, więc najpierw je zapisz. Działa także przy wyłączonych powiadomieniach.
        </p>
        <button
          onClick={sendTest} disabled={testing} data-testid="telegram-test"
          className="px-6 py-2.5 rounded-xl border border-[#2B2B2B] text-[#2B2B2B] text-sm disabled:opacity-50"
        >
          {testing ? "Wysyłanie…" : "Wyślij test"}
        </button>
        {health?.last_success_at && (
          <p className="text-xs text-[#5C6670] mt-3">
            Ostatnia udana wysyłka: {new Date(health.last_success_at).toLocaleString("pl-PL")}
          </p>
        )}
      </div>
    </div>
  );
}

function TelegramHelp() {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[#E5E0D8] rounded-2xl overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        data-testid="telegram-help-toggle"
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
      >
        <span className="text-sm text-[#2B2B2B]">Jak to wypełnić</span>
        <span className="text-[#8A9A86] text-sm shrink-0">{open ? "zwiń" : "rozwiń"}</span>
      </button>

      {open && (
        <div className="px-5 pb-6 space-y-6 border-t border-[#E5E0D8] pt-5" data-testid="telegram-help-body">
          <div>
            <p className="text-overline mb-3">Krok 1 — załóż bota</p>
            <ol className="space-y-2 pl-5">
              {[
                "Otwórz Telegram i wyszukaj @BotFather.",
                "Napisz do niego /newbot i podaj nazwę bota, gdy zapyta.",
                "BotFather odpowie tokenem — ciąg cyfr, dwukropek i litery, np. 123456789:ABCdefGhIJKlmnOPQRstuVwxYZ. Wklej go poniżej jako Token bota.",
              ].map((t, i) => (
                <li key={i} className="text-sm text-[#2B2B2B] leading-relaxed list-decimal marker:text-[#8A9A86]">{t}</li>
              ))}
            </ol>
          </div>

          <div className="border-t border-[#E5E0D8] pt-5">
            <p className="text-overline mb-3">Krok 2 — znajdź Chat ID</p>
            <ol className="space-y-2 pl-5">
              {[
                "Wyszukaj w Telegramie swojego nowego bota po nazwie i napisz do niego cokolwiek (np. „cześć”) — bot musi dostać pierwszą wiadomość.",
                "W przeglądarce otwórz adres: https://api.telegram.org/bot<TWÓJ_TOKEN>/getUpdates (podmień <TWÓJ_TOKEN> na token z kroku 1).",
                "W odpowiedzi znajdź fragment \"chat\":{\"id\":...} — ta liczba to Chat ID. Wklej ją poniżej.",
              ].map((t, i) => (
                <li key={i} className="text-sm text-[#2B2B2B] leading-relaxed list-decimal marker:text-[#8A9A86]">{t}</li>
              ))}
            </ol>
            <p className="text-sm text-[#5C6670] leading-relaxed mt-3">
              Jeśli odpowiedź jest pusta (<span className="font-mono">"result":[]</span>), to znaczy że bot jeszcze
              nie dostał żadnej wiadomości — wróć do kroku 1.
            </p>
          </div>

          <div className="border-t border-[#E5E0D8] pt-5">
            <p className="text-overline mb-3">Kolejność</p>
            <ol className="space-y-2 pl-5">
              {[
                "Wypełnij pola i kliknij Zapisz ustawienia.",
                "Kliknij Wyślij test i sprawdź czat z botem. Test działa także przy wyłączonych powiadomieniach.",
                "Gdy wiadomość testowa dojdzie, zaznacz Wysyłaj powiadomienia i zapisz ponownie.",
              ].map((t, i) => (
                <li key={i} className="text-sm text-[#2B2B2B] leading-relaxed list-decimal marker:text-[#8A9A86]">{t}</li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-overline mb-2">{label}</label>
      {children}
      {hint && <p className="text-xs text-[#5C6670] mt-1.5 leading-relaxed">{hint}</p>}
    </div>
  );
}
